import { useCallback, useEffect, useState } from "react";
import { Check, RotateCcw, X } from "lucide-react";
import type { Paginated, UserDto } from "@gatiq/shared";
import {
  Avatar,
  Button,
  Card,
  EmptyState,
  Modal,
  Pagination,
  RowActions,
  TD,
  TDStacked,
  TH,
  THead,
  TR,
  Table,
  TableSkeleton,
  Tabs,
  TextArea,
  Toolbar,
  ToolbarCount,
  useToast,
} from "@/ui";
import { api, ApiClientError } from "@/lib/api";
import { ListFilters, useListFilters } from "./ListFilters";
import { useCan } from "@/auth/access";

type Tab = "pending" | "approved" | "rejected";

const PAGE_SIZE = 10;

/**
 * The approval queue: a list page like the others, with Pending / Approved /
 * Rejected in its toolbar. Who may see it and decide on it is the KYC
 * approvals grant (D-16) — the org admin by default.
 */
export function ApprovalsInbox() {
  const toast = useToast();
  // Viewing the queue and deciding on it are separate grants (D-16).
  const canDecide = Boolean(useCan()("approvals", "edit"));
  const [tab, setTab] = useState<Tab>("pending");
  const [page, setPage] = useState(1);
  const filters = useListFilters(() => setPage(1));
  const [data, setData] = useState<Paginated<UserDto> | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rejectTarget, setRejectTarget] = useState<UserDto | null>(null);
  const [reason, setReason] = useState("");
  const [rejecting, setRejecting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const query = new URLSearchParams({ tab, page: String(page), pageSize: String(PAGE_SIZE) });
      filters.apply(query);
      const result = await api.get<Paginated<UserDto>>(
        `/org/approvals?${query}`,
      );
      setData(result);
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : "Could not load approvals.");
    } finally {
      setLoading(false);
    }
  }, [tab, page, filters.apply, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  function changeTab(value: string): void {
    setTab(value as Tab);
    setPage(1);
  }

  async function approve(karigar: UserDto): Promise<void> {
    setBusyId(karigar.id);
    try {
      await api.post(`/org/approvals/${karigar.id}/approve`, { action: "approve" });
      toast.success(`${karigar.name} approved.`);
      await load();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : "Could not approve this karigar.");
    } finally {
      setBusyId(null);
    }
  }

  async function resubmit(karigar: UserDto): Promise<void> {
    setBusyId(karigar.id);
    try {
      await api.post(`/org/approvals/${karigar.id}/resubmit`, {});
      toast.success(`${karigar.name} is back in the pending queue.`);
      await load();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : "Could not send this karigar back for approval.");
    } finally {
      setBusyId(null);
    }
  }

  async function reject(): Promise<void> {
    if (!rejectTarget) return;
    setRejecting(true);
    try {
      await api.post(`/org/approvals/${rejectTarget.id}/reject`, {
        action: "reject",
        reason: reason.trim(),
      });
      toast.success(`${rejectTarget.name} rejected.`);
      setRejectTarget(null);
      setReason("");
      await load();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : "Could not reject this karigar.");
    } finally {
      setRejecting(false);
    }
  }

  const emptyMessage =
    tab === "pending"
      ? "Nothing is waiting for your approval right now."
      : `No ${tab} karigars in your scope yet.`;

  const when = (iso: string) =>
    new Date(iso).toLocaleString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  // Pending: approve or reject. Rejected: send back for another look.
  const decide = (tab === "pending" || tab === "rejected") && canDecide;

  return (
    <>
      <Card fill>
        <Toolbar trailing={data ? <ToolbarCount count={data.total} noun="karigar" /> : null}>
          <Tabs
            items={[
              { value: "pending", label: "Pending", count: tab === "pending" ? data?.total : undefined },
              { value: "approved", label: "Approved" },
              { value: "rejected", label: "Rejected" },
            ]}
            onChange={changeTab}
            value={tab}
          />
          <ListFilters definitions={data?.filters} filters={filters} />
        </Toolbar>

        {loading && !data ? (
          <div className="min-h-0 flex-1 overflow-y-auto">
            <TableSkeleton cols={5} />
          </div>
        ) : !data || data.items.length === 0 ? (
          <div className="min-h-0 flex-1 overflow-y-auto">
            <EmptyState message={emptyMessage} title={tab === "pending" ? "All caught up" : `Nobody ${tab} yet`} />
          </div>
        ) : (
          <Table fill filterable={false}>
            <THead>
              <TR>
                <TH>Karigar</TH>
                <TH>Mobile</TH>
                <TH>Counter</TH>
                <TH>Submitted</TH>
                {tab === "rejected" ? <TH>Reason</TH> : null}
                {decide ? (
                  <TH align="right" width="4.5rem">
                    <span className="sr-only">Actions</span>
                  </TH>
                ) : null}
              </TR>
            </THead>
            <tbody>
              {data.items.map((k) => (
                <TR key={k.id}>
                  <TDStacked
                    avatar={<Avatar name={k.name} src={k.photoUrl} />}
                    primary={k.name}
                    secondary={k.displayId ? <span className="font-mono">{k.displayId}</span> : undefined}
                  />
                  <TD className="whitespace-nowrap">{k.phone ?? "—"}</TD>
                  <TD>{k.counterName ?? "—"}</TD>
                  <TD className="whitespace-nowrap text-muted">{when(k.createdAt)}</TD>
                  {tab === "rejected" ? <TD className="text-danger">{k.rejectionReason ?? "—"}</TD> : null}
                  {decide ? (
                    <TD align="right">
                      <RowActions
                        busy={busyId === k.id}
                        items={
                          tab === "rejected"
                            ? [{ label: "Send back for approval", icon: RotateCcw, onSelect: () => void resubmit(k) }]
                            : [
                                { label: "Approve", icon: Check, onSelect: () => void approve(k) },
                                {
                                  label: "Reject",
                                  icon: X,
                                  danger: true,
                                  onSelect: () => {
                                    setRejectTarget(k);
                                    setReason("");
                                  },
                                },
                              ]
                        }
                        label={k.name}
                      />
                    </TD>
                  ) : null}
                </TR>
              ))}
            </tbody>
          </Table>
        )}

        {data ? (
          <div className="shrink-0">
            <Pagination onChange={setPage} page={data.page} total={data.total} totalPages={data.totalPages} />
          </div>
        ) : null}
      </Card>

      <Modal
        footer={
          <>
            <Button onClick={() => setRejectTarget(null)} variant="secondary">
              Cancel
            </Button>
            <Button
              disabled={reason.trim().length < 4}
              loading={rejecting}
              onClick={() => void reject()}
              variant="danger"
            >
              Reject
            </Button>
          </>
        }
        onClose={() => setRejectTarget(null)}
        open={Boolean(rejectTarget)}
        title={`Reject ${rejectTarget?.name ?? ""}`}
        width="sm"
      >
        <TextArea
          label="Reason"
          onChange={(e) => setReason(e.target.value)}
          placeholder="Photo is unclear. Please submit a clear photo of your face."
          value={reason}
        />
      </Modal>
    </>
  );
}
